import { Typography } from '@startuptools/ui/Typography';
import { Stack } from '@startuptools/ui/Stack';
import { Tabs } from '@startuptools/ui/Tabs';
import { TabList } from '@startuptools/ui/TabList';
import { Tab, tabClasses } from '@startuptools/ui/Tab';
import { Button } from '@startuptools/ui/Button';
import { Box } from '@startuptools/ui/Box';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ShareIssueDocument, useShareIssueSuspenseQuery } from './ShareIssue.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Trans, useLingui } from '@lingui/react';
import { Suspense, useState } from 'react';
import { ConnectSnrCaseDialog } from '../../components/connect-snr-case-dialog/ConnectSnrCaseDialog';
import { type GqlSnrCase, GqlSnrCaseSubjectKind } from '../../graphql/base-types.graphql';
import { useSirSetSnrCaseMutation } from '../../graphql/react-operations';
import { toast } from '@startuptools/ui/toast/use-toast';
import { MeetingKindHuman } from '../meetings/components/MeetingKindHuman';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { siEditLink } from '../../components/Links';
import { useNavigate } from '../../components/Navigate';

export const ShareIssueShow = ({ shareIssueId }: { shareIssueId: string }) => {
  const companyId = useCompanyId();
  const location = useLocation();
  const { i18n } = useLingui();
  const { link } = useNavigate();
  const [openConnectCaseDialog, setOpenConnectCaseDialog] = useState(false);
  // eslint-disable-next-line sut/loading-mutation
  const [doConnectSnrCase] = useSirSetSnrCaseMutation({ refetchQueries: [ShareIssueDocument] });
  const {
    data: { shareIssue },
  } = useShareIssueSuspenseQuery({ variables: { companyId, shareIssueId }, fetchPolicy: 'cache-and-network' });

  const sir = shareIssue.shareIssueRegistrations[0]!;
  const snrCase = sir.snrCase;
  const meeting = shareIssue.meetingConcern?.meeting;
  const tabValue = location.pathname.split('/').at(-1);

  const handleConnectSnrCase = async ({ id }: Pick<GqlSnrCase, 'id'>) => {
    try {
      await doConnectSnrCase({ variables: { companyId, input: { snrCaseId: id, id: sir.id } } });
      setOpenConnectCaseDialog(false);
    } catch {
      toast({
        title: <Trans id="Error!" />,
        description: <Trans id="Could not connect case to the share issue" />,
        severity: 'danger',
      });
    }
  };

  return (
    <>
      <Stack gap={3}>
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Typography level="h2">
              <Trans id="Share Issue {description}" values={{ description: `"${shareIssue.description}"` }} />
            </Typography>
            {meeting && (
              <Typography fontSize={18}>
                <MeetingKindHuman>{meeting.kind}</MeetingKindHuman> -{' '}
                <DateFormat locale={i18n.locale}>{meeting.startTime}</DateFormat>
              </Typography>
            )}
          </Stack>

          <Box>
            <Stack direction="row" gap={2}>
              {snrCase ? (
                <Stack direction="row" gap={2}>
                  <Stack>
                    <Typography>
                      {snrCase.filingNumber}/{snrCase.filingYear}
                    </Typography>
                    <>
                      {snrCase.status.map(s => (
                        <Typography key={s.kod}>{s.value}</Typography>
                      ))}
                    </>
                  </Stack>
                  <Box>
                    <Button
                      variant="outlined"
                      color="neutral"
                      onClick={() =>
                        doConnectSnrCase({ variables: { companyId, input: { id: sir.id, snrCaseId: null } } })
                      }
                    >
                      <Trans id="Disconnect Case" />
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <>
                  <Button color="neutral" variant="outlined" onClick={() => setOpenConnectCaseDialog(true)}>
                    <Trans id="Connect Case" />
                  </Button>
                  <Button color="neutral" variant="outlined" component={Link} to={link(siEditLink(shareIssue))}>
                    <Trans id="Edit" />
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        </Stack>

        <Tabs value={tabValue}>
          <TabList
            size="lg"
            disableUnderline
            sx={theme => ({
              p: 0.5,
              gap: 0.5,
              borderRadius: 'lg',
              bgcolor: 'background.level1',
              [`& .${tabClasses.root}[aria-selected="true"]`]: {
                boxShadow: 'sm',
                bgcolor: 'background.surface',
                color: theme.palette.primary[500],
              },
            })}
          >
            <Link to="summary" relative="path">
              <Tab disableIndicator value="summary">
                <Trans id="Summary" />
              </Tab>
            </Link>
            <Link to="documents" relative="path">
              <Tab disableIndicator value="documents">
                <Trans id="Documents" />
              </Tab>
            </Link>
            <Link to="participants" relative="path">
              <Tab disableIndicator value="participants">
                <Trans id="Participants" />
              </Tab>
            </Link>
          </TabList>
        </Tabs>

        <Suspense>
          <Outlet />
        </Suspense>
      </Stack>

      {shareIssue.meetingConcern && openConnectCaseDialog && (
        <ConnectSnrCaseDialog
          subjectKinds={[GqlSnrCaseSubjectKind.Okny]}
          fromDate={shareIssue.meetingConcern.meeting.startTime}
          onClick={handleConnectSnrCase}
          onClose={() => setOpenConnectCaseDialog(false)}
        />
      )}
    </>
  );
};
